import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import _ from 'lodash'
import SearchBar from '@components/SearchBar/index.js'
import Table, {TableColumn, TableRef} from '@components/UI/table/index.js'
import {Alert, Panel, Row, Colors, IconButton, ToolTip, P, S1, Icon, Button, Popup} from '@components/UI/index.js'
import {OrderTemplate, Customer, Reseller} from 'types/types.js'
import OrderActions from 'src/actions/OrderActions.js'
import t, {getLocale} from 'src/helpers/Languages.js'
import languages from 'server/languages.js'
import {RRule} from 'rrule'
import {Query} from '@components/SearchBar/SearchItem'
import filter from 'src/utils/filter'
import {RouteTemplate} from 'server/functions/route/route.types'
import socket from '../../socketio/socketio.js'
import branchesAvailable from '@utils/branchesAvailable.js'
import {formSpacing} from '../../components/UI/forms/index.js'

type OrderTemplatesProps = {
    reseller: Reseller
    customers: {
        [customerId: string]: Customer
    }
    routeTemplates: RouteTemplate[]
    onNewTemplateClick: () => void
    onEditTemplateClick: (template: OrderTemplate) => void
    onCopyTemplateClick: (template: OrderTemplate) => void
}

function OrderTemplates({reseller, customers, onNewTemplateClick, onEditTemplateClick, onCopyTemplateClick, routeTemplates}: OrderTemplatesProps) {
    const [orderTemplates, setOrderTemplates] = useState<OrderTemplate[]>([])
    const [error, setError] = useState<string | null>(null)
    const [selectedTemplates, setSelectedTemplates] = useState<string[]>([])
    const [orderTemplateQueries, setOrderTemplateQueries] = useState<Query[]>([])
    const [loading, setLoading] = useState(false)
    const popupRef = useRef(null)
    const tableRef = useRef<TableRef>(null)

    const getTemplates = async () => {
        return new Promise<void>((resolve, reject) => {
            OrderActions.getTemplates({}, (err: string | null, templates: OrderTemplate[]) => {
                if (err) {
                    setError(err)
                    reject(err)
                    return
                }

                setOrderTemplates(templates)
                resolve()
            })
        })
    }

    useEffect(() => {
        getTemplates()

        socket.on('updateOrderTemplates', getTemplates)

        return () => {
            socket.off('updateOrderTemplates', getTemplates)
        }
    }, [])

    const onRemoveTemplates = useCallback(async () => {
        if (selectedTemplates.length === 0) return

        popupRef.current?.open(t('deleteOrderTemplate'), t('deleteOrderTemplateText'), async () => {
            setLoading(true)

            for (const templateId of selectedTemplates) {
                await new Promise<void>((resolve, reject) => {
                    OrderActions.removeTemplate(templateId, (err) => {
                        if (err) {
                            reject(err)
                        } else {
                            resolve()
                        }
                    })
                })
            }

            setSelectedTemplates([])
            await getTemplates()
            setLoading(false)
            popupRef.current?.close()
        }
        )
    }, [selectedTemplates, orderTemplates])

    const columns = useMemo((): TableColumn<OrderTemplate>[] => {
        return [
            ...branchesAvailable(reseller) ?
                [{id: 'branchName', title: t('branch'), visible: false, key: 'branchName', width: 180, text: (orderTemplate: OrderTemplate) => {
                    const branch = reseller.settings.branches?.find((branch) => branch.id === orderTemplate?.branchId)
                    return branch?.name || ''
                }}] :
                [],
            {
                id: 'name',
                title: t('name'),
                key: 'name',
                visible: true,
                width: 150
            },
            {
                id: 'rrule',
                title: t('repetition'),
                key: 'rrule',
                visible: true,
                width: 250,
                text: (template: OrderTemplate): string => {
                    const locale = getLocale()
                    const language = locale === 'en' ? languages.ENGLISH : languages.DUTCH
                    const text = `${RRule.fromString(template.rrule).toText((t) => {
                        return language.rrule[t as string] || t
                    }, language)}`
                    return _.capitalize(text)
                }
            },
            {
                id: 'routeTemplate',
                title: t('connectedRouteTemplate'),
                key: 'routeTemplate',
                visible: true,
                text: (template: OrderTemplate): string => {
                    return routeTemplates
                        .filter((rTemplate) => rTemplate.connectedRecurringOrders.includes(template._id))
                        .map((rTemplate) => rTemplate.name)
                        .join(', ')
                }
            },
            {
                id: 'customer',
                title: t('customer'),
                key: 'customer',
                visible: true,
                text: (template: OrderTemplate): string => {
                    return customers[template.customer]?.name || template.customer
                }
            },
            {
                id: 'address',
                title: t('address'),
                key: 'address',
                visible: true,
                render: (template: OrderTemplate) => {
                    const firstAddress = template.addresses[0]
                    const lastAddress = template.addresses.length > 1 ? template.addresses[template.addresses.length - 1] : null

                    return (
                        <div style={{width: '100%'}}>
                            {firstAddress &&
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <i style={{color: Colors[firstAddress.type], fontSize: 18, marginRight: 12}} className={firstAddress.type === 'pickup' ? 'mdi mdi-alpha-p-circle' : 'mdi mdi-alpha-d-circle'} />

                                    {!firstAddress.position &&
                                        <ToolTip text='Adres niet gevonden. Controleer de gegevens voor je bevestigt.'>
                                            <Icon size='md' style={{marginRight: 6, color: Colors.errorBright}} icon='mdi mdi-alert' />
                                        </ToolTip>
                                    }

                                    <P ellipsis style={{width: 150, marginRight: 6}}>
                                        {firstAddress.name}
                                    </P>

                                    <S1 ellipsis style={{marginRight: 6}}>
                                        {firstAddress.stopNumber}
                                    </S1>

                                    <P ellipsis style={{flex: 1}}>
                                        {`${firstAddress.street} ${firstAddress.nr}${firstAddress.addition} ${firstAddress.city}`.trim()}
                                    </P>

                                    {template.addresses.length > 2 &&
                                        <S1 style={{marginRight: 15}}>+ {template.addresses.length - 2}</S1>
                                    }
                                </div>
                            }

                            {lastAddress &&
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <i style={{color: Colors[lastAddress.type], fontSize: 18, marginRight: 12}} className={lastAddress.type === 'pickup' ? 'mdi mdi-alpha-p-circle' : 'mdi mdi-alpha-d-circle'} />

                                    {(_.some(template.addresses.slice(1), (address) => !address.position) || !lastAddress.position) &&
                                        <Icon size='md' style={{marginRight: 6, color: Colors.errorBright}} icon='mdi mdi-alert' />
                                    }

                                    <P ellipsis style={{width: 150, marginRight: 6}}>
                                        {lastAddress.name}
                                    </P>

                                    <S1 ellipsis style={{marginRight: 6}}>
                                        {lastAddress.stopNumber}
                                    </S1>

                                    <P ellipsis style={{flex: 1}}>
                                        {`${lastAddress.street} ${lastAddress.nr}${lastAddress.addition} ${lastAddress.city}`.trim()}
                                    </P>
                                </div>
                            }
                        </div>
                    )
                }
            },
            // {
            //     title: t('lastUpdated'),
            //     key: 'lastUpdated',
            //     visible: true,
            //     text: (template: OrderTemplate): string => {
            //         return '-'
            //     }
            // },
            {
                id: 'edit', visible: true, width: 50, showOnHover: true, render: (template: OrderTemplate) => {
                    return (
                        <IconButton onClick={() => {
                            onEditTemplateClick(template)
                        }}
                        >
                            <i className='mdi mdi-pencil' />
                        </IconButton>
                    )
                }
            }
        ]
    }, [reseller, customers, routeTemplates, onEditTemplateClick])

    const filteredTemplates = useMemo(() => {
        return filter.orderTemplates(orderTemplates, orderTemplateQueries, customers, routeTemplates, reseller) as OrderTemplate[]
    }, [orderTemplates, orderTemplateQueries, customers, routeTemplates, reseller])

    return (
        <>
            {error && (
                <Alert style={{marginBottom: 6}} variant='error'>{error}</Alert>
            )}
            <Panel style={{display: 'flex', borderTop: 'none', backgroundColor: Colors.backgroundNeutral, padding: '20px 10px 10px 10px'}}>
                <SearchBar
                    columns={columns.filter((column) => !!column.title)}
                    queries={orderTemplateQueries}
                    onQueryChange={(queries) => {
                        if (tableRef.current) {
                            tableRef.current.selectPage(1)
                        }
                        setOrderTemplateQueries([...queries])
                    }}
                />

                <Row style={{columnGap: formSpacing.columnGap}}>
                    <Button
                        variant='outline-white'
                        onClick={onNewTemplateClick}
                        // icon='mdi mdi-plus'
                        label={t('newOrderTemplate')}
                    />

                    {selectedTemplates.length === 1 &&
                        <Button
                            variant='outline-white'
                            onClick={() => {
                                const template = orderTemplates.find((t) => t._id === selectedTemplates[0])
                                if (template) {
                                    onCopyTemplateClick(template)
                                    setSelectedTemplates([])
                                } else {
                                    setError('Template niet gevonden.')
                                }
                            }}
                            tooltip='Rit template kopiëren'
                        >
                            <i className='mdi mdi-content-copy' />
                        </Button>
                    }

                    {selectedTemplates.length > 0 && (
                        <Button
                            variant='outline-white'
                            onClick={onRemoveTemplates}
                            icon='mdi mdi-delete'
                            // label={t('delete')}
                            // disabled={selectedTemplates.length === 0}
                            loading={loading}
                        />
                    )}
                </Row>
            </Panel>

            <div style={{flex: 1, marginTop: 24, marginLeft: 24, marginRight: 24}}>
                <Table
                    tableName='orderTemplates'
                    rows={filteredTemplates}
                    columns={columns}
                    selectedRows={selectedTemplates}
                    onChange={setSelectedTemplates}
                    loading={loading}
                    ref={tableRef}
                />
            </div>
            <Popup ref={popupRef} />
        </>
    )
}

export default OrderTemplates
